import { useEffect, useRef } from "react";
import heroImage from "@/assets/forno-cal-minas-gerais.webp";
import type { HeroRipple } from "@/lib/heroRipple";

/** The photograph stays visible even when WebGL is unavailable. */
const LiquidHeroImage = () => {
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const image = imageRef.current;
    const canvas = canvasRef.current;
    const section = image?.closest("section");
    if (!image || !canvas || !section) return;

    const eligible = window.matchMedia(
      "(min-width: 768px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)"
    );
    let effect: HeroRipple | null = null;
    let disposed = false;
    let starting = false;
    let failed = false;
    let visible = true;
    let latestPointer = { x: 0, y: 0 };

    const onPointerMove = async (event: PointerEvent) => {
      if (event.pointerType !== "mouse" || !eligible.matches || !visible || document.hidden || failed) return;
      latestPointer = { x: event.clientX, y: event.clientY };
      if (effect) {
        effect.move(latestPointer.x, latestPointer.y);
        return;
      }
      if (starting || !image.complete || !image.naturalWidth) return;
      starting = true;
      try {
        // No animation code or GPU context is needed for the initial page load.
        const { createHeroRipple } = await import("@/lib/heroRipple");
        if (disposed || !eligible.matches || !visible || document.hidden) return;
        effect = createHeroRipple(canvas, image);
        if (effect) effect.move(latestPointer.x, latestPointer.y);
        else failed = true;
      } catch {
        failed = true;
      } finally {
        starting = false;
      }
    };
    const onPreferenceChange = () => {
      effect?.destroy();
      effect = null;
    };
    const onVisibilityChange = () => {
      if (document.hidden) effect?.pause();
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (!visible) effect?.pause();
    });
    observer.observe(section);
    section.addEventListener("pointermove", onPointerMove, { passive: true });
    eligible.addEventListener("change", onPreferenceChange);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      disposed = true;
      observer.disconnect();
      section.removeEventListener("pointermove", onPointerMove);
      eligible.removeEventListener("change", onPreferenceChange);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      effect?.destroy();
    };
  }, []);

  return (
    <>
      <img
        ref={imageRef}
        src={heroImage}
        alt="Imagem ilustrativa de forno de cal industrial, com torre vertical e correias transportadoras"
        className="absolute inset-0 h-full w-full object-cover"
        loading="eager"
        fetchPriority="high"
        decoding="async"
        width={1672}
        height={941}
      />
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        data-hero-ripple="idle"
        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
      />
    </>
  );
};

export default LiquidHeroImage;
